//#region TYPES

export type RecursiveStepFunc = (
  thisFunc: RecursiveStepFunc,
  o: Record<string, unknown>,
  kv?: Record<string, unknown>
) => Record<string, unknown>;

type StrOrNum = string | number;

export interface ObjectCleanableFlags {
  emptyArray?: boolean;
  emptyObj?: boolean;
  emptyStr?: boolean;
  null?: boolean;
  undefinedV?: boolean;
  zero?: boolean | '0';
}

//#endregion

//#region CLEANING / COPYING

export const cleanObj = (
  obj: Record<string, unknown>,
  cleanedValues: ObjectCleanableFlags | 'all' = { null: true, undefinedV: true }
): number => {
  const cleanChecks: ((v: unknown) => boolean)[] = [];
  let cv: ObjectCleanableFlags;

  if (cleanedValues === 'all') {
    cv = { null: true, undefinedV: true, emptyStr: true, zero: '0', emptyArray: true, emptyObj: true };
  } else {
    cv = cleanedValues;
  }

  if (cv.null) cleanChecks.push((v) => v === null);
  if (cv.undefinedV) cleanChecks.push((v) => v === undefined);
  if (cv.emptyStr) cleanChecks.push((v) => v === '');
  if (cv.zero) cleanChecks.push((v) => typeof v === 'number' || typeof v === 'string' ? +v === 0 : false);
  if (cv.emptyArray) cleanChecks.push((v) => Array.isArray(v) && v.length === 0);
  if (cv.emptyObj) cleanChecks.push((v) => v !== null && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  if (cleanChecks.length === 0) throw new Error('No checks provided to cleanObj');

  let cleans = 0;

  const recursiveStep: RecursiveStepFunc = (rs, o) => {
    for (const key of Object.keys(o)) {
      if (o[key] && typeof o[key] === 'object' && !Array.isArray(o[key])) {
        recursiveStep(rs, o[key] as Record<string, unknown>);
      }

      for (const check of cleanChecks) {
        if (check(o[key])) {
          delete o[key];
          cleans++;
          break;
        }
      }
    }
    return o;
  };

  recursiveStep(recursiveStep, obj);
  return cleans;
};

export const copyObj = <T>(
  obj: T & Record<string, unknown>,
  cleanedValues?: ObjectCleanableFlags | 'all'
): T => {
  if (cleanedValues) cleanObj(obj, cleanedValues);

  const recursiveStep: RecursiveStepFunc = (rs, o) => {
    const copy: Record<string, unknown> = {};
    for (const key of Object.keys(o)) {
      copy[key] = valueCopy(rs, o[key]);
    }
    return copy;
  };

  return recursiveStep(recursiveStep, obj) as T;
};

export const updateObj = <T, U>(
  obj: T & Record<string, unknown>,
  update: Partial<T> | U,
  inPlace = false
): T & U => {
  const value: T = inPlace ? obj : copyObj(obj);

  const recursiveStep: RecursiveStepFunc = (rs, o, updates) => {
    if (!updates) return o;

    for (const k of Object.keys(updates)) {
      o[k] = valueCopy(rs, updates[k]);
    }
    return o;
  };

  recursiveStep(recursiveStep, value as Record<string, unknown>, update as Record<string, unknown>);
  return value as T & U;
};

//#endregion

//#region INIT / KEYS

export const initObj = <T = unknown, V = unknown>(
  path: (string | number)[],
  value?: V,
  obj?: T
): Record<string | number, T | Record<string | number, unknown>> => {
  const workObj: Record<string | number, unknown> = obj ?? {};
  let o: Record<string | number, unknown> = workObj;

  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    if (!o[key] || typeof o[key] !== 'object') o[key] = {};
    o = o[key] as Record<string | number, unknown>;
  }

  const lastKey = path[path.length - 1];
  if (value !== null && value !== undefined) {
    o[lastKey] = value;
  } else {
    if (!o[lastKey]) o[lastKey] = {};
  }

  return workObj as Record<string | number, T | Record<string | number, unknown>>;
};

export const objLen = (obj: Record<string, unknown>): number | null =>
  obj ? Object.keys(obj).length : null;

export const objSwitchKey = (obj: Record<string, unknown>, oldKey: StrOrNum, newKey: StrOrNum, removeOldKey = true): void => {
  obj[newKey] = obj[oldKey];
  if (removeOldKey) delete obj[oldKey];
};

export const objReKey = (
  obj: Record<string, unknown>,
  keyReplacements: Record<StrOrNum, StrOrNum> | [StrOrNum, StrOrNum][] | ((k: StrOrNum) => StrOrNum | null),
  removeOldKeys = true
): void => {
  if (Array.isArray(keyReplacements)) {
    keyReplacements.forEach(([oldKey, newKey]) => { objSwitchKey(obj, oldKey, newKey, removeOldKeys); });
  } else if (typeof keyReplacements === 'object') {
    Object.keys(keyReplacements).forEach((oldKey) => {
      objSwitchKey(obj, oldKey, keyReplacements[oldKey], removeOldKeys);
    });
  } else if (typeof keyReplacements === 'function') {
    Object.keys(obj).forEach((oldKey) => {
      const newKey = keyReplacements(oldKey);
      if (newKey !== null && newKey !== oldKey) objSwitchKey(obj, oldKey, newKey, removeOldKeys);
    });
  }
};

//#endregion

//#region FILTERS

export const filters2Filter = (
  filters: ((element: unknown) => boolean)[],
  assertNotEmpty = true
): ((element: unknown) => boolean) => {
  if (assertNotEmpty && (!filters || !filters.length)) {
    throw new Error(`No filters passed to filters2Filter. Got type: ${typeof filters}`);
  }

  return (element: unknown) => filters.every((f) => f(element));
};

//#endregion

//#region ARRAY UTILITIES

export const arrayInsert = <T>(
  arr: T[],
  index: number,
  value: T | T[],
  inPlace = false
): T[] => {
  const target = inPlace ? arr : arr.slice();
  const insertValues = Array.isArray(value) ? value : [value];
  target.splice(index, 0, ...insertValues);
  return target;
};

export const arrayRemove = <T>(
  arr: T[],
  element: T | ((element: T) => boolean),
  fromIndex = 0,
  reverse?: boolean,
  inPlace = true
): number => {
  const target = inPlace ? arr : arr.slice();
  let idx = -1;
  const fi = reverse ? target.length - 1 - fromIndex : fromIndex;

  if (typeof element === 'function') {
    const find = element as (el: T) => boolean;
    if (reverse) {
      for (let i = fi; i >= 0; i--) {
        if (find(target[i])) {
          idx = i;
          break;
        }
      }
    } else {
      for (let i = fi; i < target.length; i++) {
        if (find(target[i])) {
          idx = i;
          break;
        }
      }
    }
  } else {
    idx = reverse ? target.lastIndexOf(element, fi) : target.indexOf(element, fi);
  }

  if (idx > -1) target.splice(idx, 1);
  return idx;
};

//#endregion

//#region RECURSIVE COPY

export const arrayCopy = <T = unknown>(
  rs: RecursiveStepFunc,
  a: T[],
  kw?: Record<string, unknown>
): T[] => a.map((v: T) => valueCopy(rs, v, kw) as T);

export const valueCopy = (
  rs: RecursiveStepFunc,
  v: unknown,
  kw?: Record<string, unknown>
): unknown => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return rs(rs, v as Record<string, unknown>, kw);
  } else if (Array.isArray(v)) {
    return arrayCopy(rs, v, kw);
  }
  return v;
};

//#endregion
